import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, AppBar, Toolbar, Typography, Button, IconButton, Paper } from '@mui/material';
import { AdminPanelSettings as AdminIcon, ChatBubbleOutline as ChatIcon } from '@mui/icons-material';
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { getUserProfile, getOrCreateUserProfile } from './utils/userService';
import { usePlatformSettings } from './hooks/usePlatformSettings';
import PatternLockWizard from './components/Wizard/PatternLockWizard';
import CareerPathWizard from './components/Wizard/CareerPathWizard';
import PaymentScreen from './components/PaymentScreen';
import Auth from './components/Auth';
import AdminDashboard from './components/Admin/AdminDashboard';
import UserMenu from './components/Header/UserMenu';
import UserSettings from './components/UserSettings';
import UserInbox from './components/UserInbox';
import AdminInbox from './components/Admin/AdminInbox';
import AIAgents from './components/Admin/AIAgents';
import UserDashboard from './components/UserDashboard';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import GoogleOneTap from './components/GoogleOneTap';
import Profile from './components/Profile/Profile';
import DeveloperPortal from './pages/DeveloperPortal';
import { sanitizeError } from './utils/errorSanitizer';

// Cyber Noir theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00FFF5', // Neon Cyan
      dark: '#00CCC4',
    },
    secondary: {
      main: '#9D00FF', // Electric Purple
      dark: '#7D00CC',
    },
    background: {
      default: '#0B0C10', // Jet Black
      paper: '#1F2833', // Gunmetal Gray
    },
    text: {
      primary: '#EDEDED', // White Smoke
      secondary: '#B0B0B0',
    },
    divider: '#2A3640',
  },
  typography: {
    fontFamily: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '0.02em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '0.02em',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          padding: '10px 24px',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.03)',
            boxShadow: '0 0 20px rgba(157,0,255,0.5)',
          },
        },
        contained: {
          background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
          color: '#0B0C10',
          fontWeight: 700,
          boxShadow: '0 0 15px rgba(0,255,245,0.3)',
          '&:hover': {
            background: 'linear-gradient(90deg, #00CCC4 0%, #7D00CC 100%)',
            boxShadow: '0 0 25px rgba(157,0,255,0.6)',
          },
        },
        outlined: {
          borderColor: '#00FFF5',
          color: '#00FFF5',
          borderWidth: '2px',
          '&:hover': {
            borderColor: '#9D00FF',
            color: '#9D00FF',
            backgroundColor: 'rgba(157,0,255,0.1)',
            borderWidth: '2px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0 0 20px rgba(0,255,245,0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 0 30px rgba(0,255,245,0.2)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #0B0C10 0%, #1F2833 100%)',
          boxShadow: '0 4px 20px rgba(0,255,245,0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: '#1F2833',
          border: '1px solid rgba(0,255,245,0.2)',
          transition: 'all 0.3s ease',
          '&:hover': {
            border: '1px solid rgba(157,0,255,0.4)',
            boxShadow: '0 0 25px rgba(157,0,255,0.3)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(0,255,245,0.3)',
              borderWidth: '2px',
            },
            '&:hover fieldset': {
              borderColor: '#00FFF5',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#9D00FF',
              boxShadow: '0 0 10px rgba(157,0,255,0.3)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 600,
        },
      },
    },
  },
});

// Main App Content Component
function AppContent() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [showCareerWizard, setShowCareerWizard] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [forecastData, setForecastData] = useState(null);
  const [careerReportData, setCareerReportData] = useState(null);
  const [reportType, setReportType] = useState('cognitive'); // 'cognitive' or 'career'
  const [authChecked, setAuthChecked] = useState(false); // Track if initial auth check is done
  const [isTransitioning, setIsTransitioning] = useState(false); // Prevent flash during navigation
  const navigate = useNavigate();
  
  // Load platform settings
  const { settings: platformSettings } = usePlatformSettings();
  
  // Calculate prices (cognitive profile defaults to reportPrice for backward compatibility)
  const currentPrice = platformSettings.betaMode 
    ? platformSettings.betaPrice 
    : platformSettings.reportPrice;
  
  // Use nullish coalescing (??) instead of || to properly handle $0 prices
  const cognitivePrice = platformSettings.reportPrice ?? 79;
  const careerPrice = platformSettings.careerReportPrice ?? 79;
  const bundlePrice = platformSettings.bundlePrice ?? 149;
  const bundleSavings = (cognitivePrice + careerPrice) - bundlePrice;
  const normalBundlePrice = cognitivePrice + careerPrice;

  // Immediate auth state check on mount (for returning users)
  useEffect(() => {
    // Check if user is already authenticated (instant check, no waiting)
    const currentUser = auth.currentUser;
    if (currentUser) {
      console.log('⚡ Immediate auth detected:', currentUser.email);
      setUser(currentUser);
      setIsAuthenticated(true);
      
      // Load profile in background
      getOrCreateUserProfile(currentUser)
        .then(profile => {
          console.log('👤 User profile loaded:', profile);
          setUserProfile(profile);
        })
        .catch(error => {
          console.error('❌ Error loading user profile:', error);
        });
    }
    setAuthChecked(true);
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      setIsAuthenticated(!!user);
      console.log('🔐 Auth state changed:', user ? `Logged in as ${user.email}` : 'Not logged in');
      
      // Create or load user profile (this creates the users collection)
      if (user) {
        try {
          const profile = await getOrCreateUserProfile(user);
          console.log('👤 User profile loaded/created:', profile);
          setUserProfile(profile);
        } catch (error) {
          console.error('❌ Error loading user profile:', error);
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
      }
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, []);

  // Process free cognitive report (when price is $0)
  const processFreeCognitiveReport = async (data) => {
    console.log('💰 Processing FREE cognitive report:', data.forecastId);
    try {
      const functions = getFunctions();
      const db = getFirestore();
      
      const forecastRef = doc(db, 'forecasts', data.forecastId);
      await updateDoc(forecastRef, {
        paymentStatus: 'completed',
        paymentAmount: 0,
        paymentDate: new Date(),
        paymentId: 'free_' + Date.now(),
        status: 'payment_complete',
        updatedAt: new Date(),
      });
      console.log('✅ Updated cognitive report for free mode');
      
      // Trigger analysis
      const analyzePatternLock = httpsCallable(functions, 'analyzePatternLock');
      analyzePatternLock({ forecastId: data.forecastId })
        .then((result) => console.log('✅ Cognitive analysis triggered:', result.data))
        .catch((error) => {
          console.error('❌ Cognitive analysis failed:', error);
          updateDoc(forecastRef, {
            status: 'failed',
            error: sanitizeError(error),
            updatedAt: new Date(),
          }).catch(console.error);
        });
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('❌ Error processing free cognitive report:', error);
      alert('Error starting report analysis. Please try again.');
    }
  };

  // Process free career report (when price is $0)
  const processFreeCareerReport = async (data) => {
    console.log('💰 Processing FREE career report:', data.careerReportId);
    
    if (!data.careerReportId) {
      console.error('❌ No careerReportId provided!');
      alert('Error: Report ID missing. Please try again.');
      return;
    }
    
    try {
      const functions = getFunctions();
      const db = getFirestore();
      
      // Update the report status
      const careerRef = doc(db, 'careerReports', data.careerReportId);
      console.log('📝 Updating Firestore document:', data.careerReportId);
      
      await updateDoc(careerRef, {
        paymentStatus: 'completed',
        paymentCompleted: true,
        paymentAmount: 0,
        paymentDate: new Date(),
        paymentId: 'free_' + Date.now(),
        status: 'analyzing',
        updatedAt: new Date(),
      });
      console.log('✅ Updated career report for free mode, status: analyzing');
      
      // Trigger career analysis - don't await but ensure it's called
      // Extended timeout for Grok web search (5 minutes)
      const analyzeCareerPath = httpsCallable(functions, 'analyzeCareerPath', { timeout: 300000 });
      console.log('🚀 Triggering career analysis Cloud Function for:', data.careerReportId);
      
      // Fire the function call
      analyzeCareerPath({ reportId: data.careerReportId })
        .then((result) => {
          console.log('✅ Career analysis triggered successfully:', result.data);
        })
        .catch((error) => {
          console.error('❌ Career analysis failed:', error);
          console.error('❌ Error code:', error.code);
          console.error('❌ Error message:', error.message);
          updateDoc(careerRef, {
            status: 'failed',
            error: sanitizeError(error),
            updatedAt: new Date(),
          }).catch(console.error);
        });
      
      // Small delay to ensure the function call is initiated before navigating
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Navigate to user's profile
      console.log('🔄 Navigating to profile...');
      navigate(`/profiles/${user.uid}_career`);
    } catch (error) {
      console.error('❌ Error processing free career report:', error);
      console.error('❌ Error details:', error.code, error.message);
      alert('Error starting report analysis: ' + sanitizeError(error));
    }
  };

  const handleComplete = (data) => {
    console.log('Forecast complete:', data);
    setForecastData(data);
    setReportType('cognitive');
    setShowWizard(false);
    
    // Check if price is zero (free mode)
    if (cognitivePrice === 0) {
      console.log('💰 FREE MODE: Skipping payment for cognitive report');
      // Process free report directly with the data
      processFreeCognitiveReport(data);
    } else {
    setShowPayment(true);
    }
  };

  const handleCareerComplete = (data) => {
    console.log('📋 Career report complete:', data);
    console.log('🆔 Career report ID:', data.careerReportId);
    
    if (!data.careerReportId) {
      console.error('❌ No careerReportId in completion data!');
      alert('Error: Career report was not saved properly. Please try again.');
      return;
    }
    
    // Set transitioning state BEFORE closing wizard to prevent landing page flash
    setIsTransitioning(true);
    setCareerReportData(data);
    setReportType('career');
    setShowCareerWizard(false);
    
    // Career Builder is now completely free - skip payment
    console.log('🆓 Career Builder is free - processing report directly');
    processFreeCareerReport(data);
  };

  const handlePaymentSuccess = async (paymentIntent, finalReportType) => {
    console.log('💰 Payment successful:', paymentIntent);
    console.log('📊 Report type:', finalReportType || reportType);
    
    const actualReportType = finalReportType || reportType;
    
    try {
      const functions = getFunctions();
      const db = getFirestore();
      
      // Handle based on report type
      if (actualReportType === 'bundle') {
        // Update both reports
        if (forecastData?.forecastId) {
          const forecastRef = doc(db, 'forecasts', forecastData.forecastId);
      await updateDoc(forecastRef, {
        paymentStatus: 'completed',
        paymentAmount: paymentIntent.amount,
        paymentDate: new Date(),
        paymentId: paymentIntent.id,
        status: 'payment_complete',
        updatedAt: new Date(),
      });
          console.log('✅ Updated cognitive report with payment info');
      
          // Trigger cognitive analysis
      const analyzePatternLock = httpsCallable(functions, 'analyzePatternLock');
          analyzePatternLock({ forecastId: forecastData.forecastId })
            .then((result) => console.log('✅ Cognitive analysis triggered:', result.data))
            .catch((error) => console.error('❌ Cognitive analysis failed:', error));
        }
        
        if (careerReportData?.careerReportId) {
          const careerRef = doc(db, 'careerReports', careerReportData.careerReportId);
          await updateDoc(careerRef, {
            paymentStatus: 'completed',
            paymentCompleted: true,
            paymentAmount: paymentIntent.amount,
            paymentDate: new Date(),
            paymentId: paymentIntent.id,
            status: 'payment_complete',
            updatedAt: new Date(),
          });
          console.log('✅ Updated career report with payment info');
          
          // Trigger career analysis (extended timeout for Grok)
          const analyzeCareerPath = httpsCallable(functions, 'analyzeCareerPath', { timeout: 300000 });
          analyzeCareerPath({ reportId: careerReportData.careerReportId })
            .then((result) => console.log('✅ Career analysis triggered:', result.data))
            .catch((error) => console.error('❌ Career analysis failed:', error));
        }
      } else if (actualReportType === 'career') {
        // Career report only
        if (careerReportData?.careerReportId) {
          const careerRef = doc(db, 'careerReports', careerReportData.careerReportId);
          
          // First update to show payment complete and set to analyzing
          await updateDoc(careerRef, {
            paymentStatus: 'completed',
            paymentCompleted: true,
            paymentAmount: paymentIntent.amount,
            paymentDate: new Date(),
            paymentId: paymentIntent.id,
            status: 'analyzing',
            updatedAt: new Date(),
          });
          console.log('✅ Updated career report with payment info, status: analyzing');
          
          // Trigger career analysis immediately (extended timeout for Grok)
          const analyzeCareerPath = httpsCallable(functions, 'analyzeCareerPath', { timeout: 300000 });
          console.log('🚀 Triggering career analysis for:', careerReportData.careerReportId);
          
          // Call the function but don't wait for it to complete
          analyzeCareerPath({ reportId: careerReportData.careerReportId })
        .then((result) => {
              console.log('✅ Career analysis triggered successfully:', result.data);
        })
        .catch((error) => {
              console.error('❌ Career analysis failed:', error);
              console.error('❌ Error details:', error.code, error.message);
              // Update status to show error - use sanitized message for user display
              updateDoc(careerRef, {
                status: 'failed',
                error: sanitizeError(error),
                updatedAt: new Date(),
              }).catch(console.error);
            });
        }
      } else {
        // Cognitive report only (default)
        if (forecastData?.forecastId) {
          const forecastRef = doc(db, 'forecasts', forecastData.forecastId);
          await updateDoc(forecastRef, {
            paymentStatus: 'completed',
            paymentAmount: paymentIntent.amount,
            paymentDate: new Date(),
            paymentId: paymentIntent.id,
            status: 'payment_complete',
            updatedAt: new Date(),
          });
          console.log('✅ Updated cognitive report with payment info');
          
          // Trigger cognitive analysis
          const analyzePatternLock = httpsCallable(functions, 'analyzePatternLock');
          console.log('🚀 Triggering cognitive analysis for:', forecastData.forecastId);
          
          analyzePatternLock({ forecastId: forecastData.forecastId })
            .then((result) => {
              console.log('✅ Cognitive analysis triggered:', result.data);
            })
            .catch((error) => {
              console.error('❌ Cognitive analysis failed:', error);
          updateDoc(forecastRef, {
            status: 'failed',
            error: sanitizeError(error),
            updatedAt: new Date(),
          }).catch(console.error);
        });
        }
      }
      
      // Small delay to ensure requests are sent
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Redirect to dashboard (correct tab based on report type)
      console.log('✅ Redirecting...');
      setShowPayment(false);
      if (actualReportType === 'career') {
        // Redirect to user's public profile so they can see and edit it
        navigate(`/profiles/${user.uid}_career`);
      } else if (actualReportType === 'bundle') {
        navigate(`/profiles/${user.uid}_career`); // Show profile for bundles
      } else {
        navigate('/dashboard?tab=cognitive'); // Cognitive only
      }
    } catch (error) {
      console.error('❌ Error after payment:', error);
      // Still redirect to dashboard even if something fails
      setShowPayment(false);
      navigate('/dashboard');
    }
  };

  const handlePaymentCancel = () => {
    setShowPayment(false);
    if (reportType === 'career') {
      setShowCareerWizard(true);
    } else {
    setShowWizard(true);
    }
  };

  const handleRequireAuth = () => {
    console.log('🔒 Authentication required, showing auth screen');
    setShowWizard(false);
    setShowCareerWizard(false);
    setShowAuth(true);
  };

  const handleAuthSuccess = () => {
    console.log('✅ Authentication successful');
    setShowAuth(false);
    // Return to the wizard that triggered auth
    if (reportType === 'career') {
      setShowCareerWizard(true);
    } else {
    setShowWizard(true);
    }
  };

  const handleAuthCancel = () => {
    setShowAuth(false);
  };

  // Show Auth Screen
  if (showAuth) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Auth
          onSuccess={handleAuthSuccess}
          onCancel={handleAuthCancel}
        />
      </ThemeProvider>
    );
  }

  // Show Payment Screen
  if (showPayment && (forecastData || careerReportData)) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PaymentScreen
          forecastData={forecastData}
          careerReportData={careerReportData}
          reportType={reportType}
          onPaymentSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
        />
      </ThemeProvider>
    );
  }

  // Show Cognitive Wizard
  if (showWizard) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
          <Container maxWidth="lg">
            <Button 
              onClick={() => setShowWizard(false)}
              sx={{ mb: 2, color: 'primary.main' }}
            >
              ← Back
            </Button>
            <PatternLockWizard
              onComplete={handleComplete}
              user={user}
              existingForecast={null}
              onRequireAuth={handleRequireAuth}
              isAuthenticated={isAuthenticated}
            />
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  // Show Career Wizard
  if (showCareerWizard) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
          <Container maxWidth="lg">
            <Button 
              onClick={() => setShowCareerWizard(false)}
              sx={{ mb: 2, color: 'primary.main' }}
            >
              ← Back
            </Button>
            <CareerPathWizard
              onComplete={handleCareerComplete}
              user={user}
              onRequireAuth={handleRequireAuth}
              isAuthenticated={isAuthenticated}
            />
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  // Show transition screen while navigating to profile (prevents landing page flash)
  if (isTransitioning) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box 
          sx={{ 
            minHeight: '100vh', 
            bgcolor: '#0a0a0a', 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center',
            gap: 3
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              border: '3px solid transparent',
              borderTopColor: '#00FFF5',
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          />
          <Typography 
            sx={{ 
              color: '#fafafa', 
              fontFamily: 'monospace',
              fontSize: '1.1rem'
            }}
          >
            Building your career profile...
          </Typography>
          <Typography 
            sx={{ 
              color: '#737373', 
              fontFamily: 'monospace',
              fontSize: '0.85rem'
            }}
          >
            This takes about 60 seconds
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      {/* Google One Tap - only show for non-authenticated users */}
      {authChecked && !isAuthenticated && (
        <GoogleOneTap 
          onSuccess={() => {
            console.log('✅ One Tap sign-in successful');
            // Auth state will be handled by onAuthStateChanged
          }}
        />
      )}
      
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar position="static">
          <Toolbar>
            <Typography 
              variant="h6" 
              sx={{ 
                flexGrow: 1, 
                cursor: 'pointer',
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
              onClick={() => navigate('/')}
            >
            {platformSettings.platformName}
          </Typography>
            {isAuthenticated && (
              <UserMenu 
                user={user} 
                userProfile={userProfile}
                onNavigate={navigate}
              />
            )}
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ mt: 8 }}>
          {/* Hero Section */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h1" 
              sx={{ 
                mb: 3, 
                fontWeight: 'bold',
                background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                letterSpacing: '0.02em',
              }}
            >
              Plan Your Next Career Move — Free
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 4, 
                color: 'text.primary',
                fontWeight: 500,
                maxWidth: '800px',
                margin: '0 auto 2rem',
              }}
            >
              A personalized training plan based on real employer demand
            </Typography>

            <Typography 
              variant="body1" 
              sx={{ 
                mb: 4, 
                color: 'text.secondary',
                maxWidth: '750px',
                margin: '0 auto 2rem',
                lineHeight: 1.8,
                fontSize: '1.1rem',
              }}
            >
              What used to require expensive career coaches is now free — powered by AI and real job market data. 
              We analyze thousands of job postings to show exactly what skills you need and how to get them.
            </Typography>

            {/* Primary CTA Button */}
            <Button
              variant="contained"
                  size="large"
                  onClick={() => {
                    setReportType('career');
                    if (isAuthenticated) {
                      setShowCareerWizard(true);
                    } else {
                      setShowAuth(true);
                    }
                  }}
                  sx={{
                    bgcolor: '#00FFF5',
                color: '#0a0a0a',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontFamily: 'monospace',
                fontWeight: 700,
                mb: 2,
                '&:hover': {
                      bgcolor: '#00CCC5',
                  boxShadow: '0 0 30px rgba(0, 255, 245, 0.5)',
                  transform: 'scale(1.02)'
                }
              }}
            >
              Build My Career Plan — Free
            </Button>
              <Typography variant="body2" sx={{ color: '#737373', fontFamily: 'monospace' }}>
              Takes 2 minutes • Personalized to your experience • No credit card required
              </Typography>
          </Box>

          {/* What You Get - Gap Analysis & Training Plan Preview */}
          <Box sx={{ mb: 10 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 2, 
                textAlign: 'center',
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              What You Get
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 6, 
                textAlign: 'center',
                color: 'text.secondary',
                maxWidth: '600px',
                margin: '0 auto 3rem'
              }}
            >
              A complete career roadmap personalized to your skills and target role
            </Typography>

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: 4,
              maxWidth: '1200px',
              margin: '0 auto'
            }}>
              {/* Gap Analysis Preview */}
              <Paper sx={{ 
                p: 4, 
                bgcolor: '#0d0d0d', 
                border: '1px solid #262626',
                borderRadius: '12px',
              }}>
                <Typography variant="h5" sx={{ color: '#fafafa', fontWeight: 700, mb: 1 }}>
                  Gap Analysis: Your Target Role
                </Typography>
                <Typography variant="body2" sx={{ color: '#737373', mb: 3 }}>
                  Skills comparison between your current experience and target role requirements
                </Typography>
                
                {/* Matched Skills */}
                <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600, mb: 1, display: 'block' }}>
                  Matched Skills
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {['Leadership', 'Collaboration', 'Communication', 'AWS', 'Azure'].map((skill) => (
                    <Box key={skill} sx={{ 
                      px: 1.5, py: 0.5, 
                      bgcolor: 'rgba(34, 197, 94, 0.15)', 
                      border: '1px solid #22c55e',
                      borderRadius: '6px',
                      color: '#22c55e',
                      fontSize: '0.8rem',
                      fontWeight: 500
                    }}>
                      {skill}
                    </Box>
                  ))}
                </Box>

                {/* Skills to Develop */}
                <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 600, mb: 1, display: 'block' }}>
                  Skills to Develop
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {['Analytical', 'Attention To Detail', 'Organization', 'Analytics', 'Problem Solving', 'Teamwork', 'Project Management'].map((skill) => (
                    <Box key={skill} sx={{ 
                      px: 1.5, py: 0.5, 
                      bgcolor: 'rgba(212, 175, 55, 0.15)', 
                      border: '1px solid #D4AF37',
                      borderRadius: '6px',
                      color: '#D4AF37',
                      fontSize: '0.8rem',
                      fontWeight: 500
                    }}>
                      {skill}
                    </Box>
                  ))}
                </Box>
              </Paper>

              {/* Training Plan Preview */}
              <Paper sx={{ 
                p: 4, 
                bgcolor: '#0d0d0d', 
                border: '1px solid #262626',
                borderRadius: '12px',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h5" sx={{ color: '#fafafa', fontWeight: 700 }}>
                    Training Plan for Your Target Role
                  </Typography>
                <Box sx={{ 
                    bgcolor: '#171717', 
                    border: '1px solid #00FFF5', 
                    borderRadius: '6px', 
                    px: 2, py: 0.5,
                    color: '#00FFF5',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                  }}>
                    0/10 Complete
                </Box>
                </Box>
                <Typography variant="body2" sx={{ color: '#737373', mb: 2 }}>
                  A job market data-driven plan based on common employer requirements
                </Typography>
                
                {/* Stats Row */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Typography variant="caption" sx={{ color: '#a1a1aa' }}>8 courses</Typography>
                  <Typography variant="caption" sx={{ color: '#D4AF37' }}>2 certifications</Typography>
                  <Typography variant="caption" sx={{ color: '#00FFF5' }}>Est. $749</Typography>
            </Box>

                {/* Sample Training Items */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Course Item */}
            <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    p: 2, 
                    bgcolor: '#171717', 
                    border: '1px solid #262626',
                    borderRadius: '8px'
                  }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #404040' }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ color: '#fafafa', fontWeight: 500 }}>
                        Enterprise Architecture Foundations
                </Typography>
                      <Typography variant="caption" sx={{ color: '#00FFF5' }}>
                        LinkedIn Learning • 2 hours
                </Typography>
                    </Box>
                    <Box sx={{ bgcolor: '#22c55e', color: '#0a0a0a', px: 1.5, py: 0.25, borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>
                      Free
                    </Box>
                  </Box>

                  {/* Certification Prep Course */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    p: 2, 
                    bgcolor: '#171717', 
                    border: '1px solid #262626',
                    borderRadius: '8px'
                  }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #404040' }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ color: '#fafafa', fontWeight: 500 }}>
                        TOGAF 9 Foundation Complete Course
                </Typography>
                      <Typography variant="caption" sx={{ color: '#00FFF5' }}>
                        Udemy • 12 hours
                </Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'rgba(0, 255, 245, 0.1)', color: '#00FFF5', px: 1.5, py: 0.25, borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, border: '1px solid #00FFF5' }}>
                      $15
                    </Box>
                  </Box>

                  {/* Certification Item */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    p: 2, 
                    bgcolor: 'rgba(212, 175, 55, 0.05)', 
                    border: '1px solid #D4AF37',
                    borderRadius: '8px'
                  }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #D4AF37' }} />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: '#fafafa', fontWeight: 600 }}>
                          TOGAF 9 Foundation
                </Typography>
                        <Box sx={{ bgcolor: '#D4AF37', color: '#0a0a0a', px: 1, py: 0.1, borderRadius: '3px', fontSize: '0.65rem', fontWeight: 700 }}>
                          ★ CERTIFICATION
                        </Box>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#D4AF37' }}>
                        The Open Group • Exam: 90 min
                </Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37', px: 1.5, py: 0.25, borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, border: '1px solid #D4AF37' }}>
                      $320
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* How It Works */}
          <Box sx={{ mb: 10 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 6, 
                textAlign: 'center',
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              How It Works
            </Typography>

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 4,
              maxWidth: '1000px',
              margin: '0 auto'
            }}>
              {/* Step 1 */}
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: '50%', 
                  bgcolor: 'rgba(0, 255, 245, 0.1)', 
                  border: '2px solid #00FFF5',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '0 auto 1.5rem',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#00FFF5',
                  fontFamily: 'monospace'
                }}>
                  1
                </Box>
                <Typography variant="h6" sx={{ color: '#fafafa', mb: 1, fontWeight: 600 }}>
                  Upload Your Resume
              </Typography>
                <Typography variant="body2" sx={{ color: '#737373' }}>
                  We extract your skills, experience, and accomplishments automatically
              </Typography>
              </Box>

              {/* Step 2 */}
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: '50%', 
                  bgcolor: 'rgba(0, 255, 245, 0.1)', 
                  border: '2px solid #00FFF5',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '0 auto 1.5rem',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#00FFF5',
                  fontFamily: 'monospace'
                }}>
                  2
                </Box>
                <Typography variant="h6" sx={{ color: '#fafafa', mb: 1, fontWeight: 600 }}>
                  Choose Your Target Role
              </Typography>
                <Typography variant="body2" sx={{ color: '#737373' }}>
                  Select a promotion, pivot, or let AI suggest your next logical step
              </Typography>
              </Box>

              {/* Step 3 */}
              <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: '50%', 
                  bgcolor: 'rgba(0, 255, 245, 0.1)', 
                  border: '2px solid #00FFF5',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '0 auto 1.5rem',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#00FFF5',
                  fontFamily: 'monospace'
                }}>
                  3
                </Box>
                <Typography variant="h6" sx={{ color: '#fafafa', mb: 1, fontWeight: 600 }}>
                  Get Your Plan + Profile
                </Typography>
                <Typography variant="body2" sx={{ color: '#737373' }}>
                  Receive your personalized training plan and shareable public profile
                </Typography>
              </Box>
              </Box>

            {/* CTA after How It Works */}
            <Box sx={{ textAlign: 'center', mt: 6 }}>
                <Button
                variant="contained"
                size="large"
                onClick={() => {
                  setReportType('career');
                  if (isAuthenticated) {
                    setShowCareerWizard(true);
                  } else {
                    setShowAuth(true);
                  }
                }}
                  sx={{
                  bgcolor: '#00FFF5',
                  color: '#0a0a0a',
                  px: 5,
                    py: 1.5,
                  fontSize: '1.1rem',
                    fontFamily: 'monospace',
                  fontWeight: 700,
                    '&:hover': {
                    bgcolor: '#00CCC5',
                    boxShadow: '0 0 25px rgba(0, 255, 245, 0.5)',
                  }
                }}
              >
                Start My Free Career Plan
                </Button>
            </Box>
              </Box>

          {/* Optional Add-On: Cognitive Profile */}
          <Box sx={{ mb: 10 }}>
            <Paper sx={{
              p: 4,
              backgroundColor: '#171717',
              border: '1px solid #262626',
              borderRadius: '12px',
              maxWidth: '800px',
              margin: '0 auto',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: 4
            }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h5" sx={{ color: '#9D00FF', fontWeight: 700, fontFamily: 'monospace' }}>
                    Cognitive Profile
              </Typography>
                  <Box sx={{ backgroundColor: '#9D00FF', color: '#fafafa', px: 1.5, py: 0.5, borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700 }}>
                    OPTIONAL ADD-ON
              </Box>
          </Box>
                <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 2 }}>
                  Want to stand out even more? Add a Cognitive Profile that reveals how you think, create, and process information. 
                  Share it with employers to differentiate yourself from other candidates.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ color: '#737373' }}>✓ 9-section PDF report</Typography>
                  <Typography variant="caption" sx={{ color: '#737373' }}>✓ Thinking style analysis</Typography>
                  <Typography variant="caption" sx={{ color: '#737373' }}>✓ Shareable with employers</Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#fafafa', fontWeight: 700, mb: 1 }}>
                  ${currentPrice}
            </Typography>
            <Button
                  variant="outlined"
              onClick={() => {
                    setReportType('cognitive');
                if (isAuthenticated) {
                  setShowWizard(true);
                } else {
                  setShowAuth(true);
                }
              }}
              sx={{
                    borderColor: '#9D00FF',
                    color: '#9D00FF',
                    px: 3,
                    py: 1,
                fontFamily: 'monospace',
                fontWeight: 700,
                '&:hover': {
                      bgcolor: 'rgba(157, 0, 255, 0.1)',
                      borderColor: '#9D00FF',
                }
              }}
            >
                  Learn More
            </Button>
              </Box>
            </Paper>
          </Box>

          {/* Footer */}
          <Box 
            sx={{ 
              borderTop: '1px solid #262626',
              pt: 6,
              pb: 6,
              mt: 8
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: 4
            }}>
              {/* Brand */}
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 1,
                    fontWeight: 700,
                    background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {platformSettings.platformName}
                </Typography>
                <Typography variant="body2" sx={{ color: '#737373', fontFamily: 'monospace' }}>
                  {platformSettings.platformTagline || 'Your mind, decoded from your words.'}
                </Typography>
              </Box>

              {/* Links */}
              <Box sx={{ 
                display: 'flex', 
                gap: 4,
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center'
              }}>
                <Typography
                  onClick={() => navigate('/privacy')}
                  sx={{ 
                    color: '#a1a1aa',
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    '&:hover': { 
                      color: '#00FFF5',
                    },
                    transition: 'color 0.3s'
                  }}
                >
                  Privacy Policy
                </Typography>
                <Typography
                  onClick={() => navigate('/terms')}
                  sx={{ 
                    color: '#a1a1aa',
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    '&:hover': { 
                      color: '#00FFF5',
                    },
                    transition: 'color 0.3s'
                  }}
                >
                  Terms of Service
                </Typography>
                <Box
                  onClick={() => {
                    if (isAuthenticated) {
                      navigate('/inbox');
                    } else {
                      setShowAuth(true);
                    }
                  }}
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: '#a1a1aa',
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    '&:hover': { 
                      color: '#00FFF5',
                    },
                    transition: 'color 0.3s'
                  }}
                >
                  <ChatIcon sx={{ fontSize: '1rem' }} />
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                    Contact Support
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Copyright */}
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#525252',
                textAlign: 'center',
                mt: 6,
                fontFamily: 'monospace',
                fontSize: '0.85rem'
              }}
            >
              © {new Date().getFullYear()} {platformSettings.companyName || 'NeurolNet Inc.'}. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

// Wrapper App component with routing
function App() {
  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route path="/" element={<AppContent />} />
          <Route path="/dashboard" element={<UserDashboard />} />
          <Route path="/settings" element={<UserSettings />} />
          <Route path="/inbox" element={<UserInbox />} />
          <Route 
            path="/admin" 
            element={
              <ProtectedAdminRoute>
                <AdminDashboard />
              </ProtectedAdminRoute>
            } 
          />
          <Route 
            path="/admin/inbox" 
            element={
              <ProtectedAdminRoute>
                <AdminInbox />
              </ProtectedAdminRoute>
            } 
          />
          <Route 
            path="/admin/ai-agents" 
            element={
              <ProtectedAdminRoute>
                <AIAgents />
              </ProtectedAdminRoute>
            } 
          />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/developer" element={<DeveloperPortal />} />
          <Route path="/profiles/:id" element={<Profile />} />
        </Routes>
      </ThemeProvider>
    </BrowserRouter>
  );
}

// Protected route for admin
function ProtectedAdminRoute({ children }) {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 Admin Route - Auth state:', user ? `User: ${user.email}` : 'No user');
      if (user) {
        const profile = await getOrCreateUserProfile(user);
        console.log('👤 Admin Route - User profile loaded:', profile);
        console.log('🎭 Admin Route - User role:', profile?.role);
        setUserProfile(profile);
      } else {
        console.log('❌ Admin Route - No authenticated user, redirecting...');
        setUserProfile(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    console.log('⏳ Admin Route - Loading user profile...');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#0a0a0a' }}>
        <Typography sx={{ color: '#fafafa', fontFamily: 'monospace' }}>Loading...</Typography>
      </Box>
    );
  }

  if (!userProfile) {
    console.log('❌ Admin Route - No user profile found, redirecting to home');
    return <Navigate to="/" replace />;
  }

  if (userProfile.role !== 'admin') {
    console.log(`❌ Admin Route - User role is "${userProfile.role}", not "admin". Redirecting...`);
    return <Navigate to="/" replace />;
  }

  console.log('✅ Admin Route - Access granted!');
  return children;
}

export default App;
